import React, { useState } from 'react';
import Sidebar from './Sidebar';
import { PageView, User, AppNotification, ToastMessage } from '../types';
// CORREÇÃO: Adicionado 'X' nas importações
import { Bell, Command, Wifi, Loader2, Activity, ShieldCheck, Settings as SettingsIcon, User as UserIcon, Globe, LogOut, Sparkles, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import CommandPalette from './ui/CommandPalette';
import ToastNotification from './ui/ToastNotification';
import AIAssistant from './AIAssistant';

// Helper para casting de Framer Motion
const MotionDiv = motion.div as any;

interface AppLayoutProps {
  user: User;
  page: PageView;
  setPage: (p: PageView) => void;
  lang: 'en' | 'pt';
  setLang: (l: 'en' | 'pt') => void;
  notifications: AppNotification[];
  isSyncing: boolean;
  onLogout: () => void;
  onOpenSettings: () => void;
  onOpenSubscription: () => void;
  children: React.ReactNode;
  toasts: ToastMessage[];
  removeToast: (id: string) => void;
}

export const AppLayout: React.FC<AppLayoutProps> = ({ 
  user, page, setPage, lang, setLang, notifications, isSyncing, onLogout, 
  onOpenSettings, onOpenSubscription, children, toasts, removeToast 
}) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isCommandOpen, setIsCommandOpen] = useState(false);
  const [isAIOpen, setIsAIOpen] = useState(false);

  const inverseScale = 100 / zoomLevel;

  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsCommandOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div 
        className="flex bg-slate-950 text-slate-100 font-sans selection:bg-emerald-500/30 selection:text-emerald-200 overflow-hidden"
        style={{ zoom: zoomLevel, width: `${inverseScale}vw`, height: `${inverseScale}vh` } as any} 
    >
      <ToastNotification toasts={toasts} removeToast={removeToast} />
      <CommandPalette 
        isOpen={isCommandOpen} 
        onClose={() => setIsCommandOpen(false)} 
        setPage={setPage} 
        onLogout={onLogout}
        actions={{ addProperty: () => setPage('properties'), addTask: () => setPage('maintenance') }}
      />

      <Sidebar 
        currentPage={page} 
        setPage={setPage} 
        lang={lang} 
        subscription={user.subscription}
        onUpgrade={onOpenSubscription}
      />
      
      <div className="flex-1 flex flex-col h-full overflow-hidden relative bg-slate-950">
        <header className="h-24 bg-slate-950/80 backdrop-blur-md border-b border-slate-800 flex items-center justify-between px-8 shrink-0 z-20">
            <div>
                <h1 className="text-2xl font-bold text-white tracking-tight capitalize">{page}</h1>
                <p className="text-xs text-slate-500 font-mono uppercase tracking-widest mt-1">Workspace / {user.name.split(' ')[0]}</p>
            </div>

            <div className="flex items-center gap-6">
                <div className="relative cursor-pointer hover:text-white text-slate-400 transition-colors">
                    <Bell className="w-5 h-5" />
                    {notifications.filter(n => !n.isRead).length > 0 && <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse"></span>}
                </div>

                <div onClick={() => setIsCommandOpen(true)} className="hidden lg:flex items-center gap-2 bg-slate-900 border border-slate-700 px-3 py-1.5 rounded-lg text-xs text-slate-400 cursor-pointer hover:border-slate-500 transition-colors">
                    <Command className="w-3 h-3" /> <span>Search</span> <span className="bg-slate-800 px-1 rounded text-[10px]">⌘K</span>
                </div>

                <div className={`flex items-center gap-2 text-xs font-medium ${isSyncing ? 'text-emerald-400' : 'text-slate-600'}`}>
                    {isSyncing ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /><span className="hidden md:inline">SYNCING...</span></> : <><Wifi className="w-3.5 h-3.5" /><span className="hidden md:inline">CONNECTED</span></>}
                </div>

                <div className="h-10 w-px bg-slate-800 hidden md:block"></div>

                <div className="relative">
                    <button onClick={() => setIsProfileOpen(!isProfileOpen)} className="flex items-center gap-3 p-1 pr-3 rounded-full hover:bg-slate-900 border border-transparent hover:border-slate-800 transition-all group">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold shadow-lg">
                            {user.name.charAt(0).toUpperCase()}
                        </div>
                    </button>

                    <AnimatePresence>
                        {isProfileOpen && (
                            <MotionDiv initial={{ opacity: 0, y: 10, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 10, scale: 0.95 }} className="absolute right-0 top-full mt-3 w-80 bg-slate-900/95 backdrop-blur-xl rounded-2xl border border-slate-700 shadow-2xl overflow-hidden z-50">
                                <div className="p-4 space-y-1">
                                    <button onClick={() => { onOpenSettings(); setIsProfileOpen(false); }} className="w-full text-left px-4 py-3 rounded-xl text-sm font-medium text-slate-300 hover:text-white hover:bg-slate-800 flex items-center gap-3">
                                        <UserIcon className="w-4 h-4" /> Personal Profile
                                    </button>
                                    <div className="flex items-center justify-between px-4 py-2">
                                        <span className="text-sm text-slate-400 flex gap-2"><Globe className="w-4 h-4"/> Lang</span>
                                        <button onClick={() => setLang(lang === 'en' ? 'pt' : 'en')} className="text-xs bg-slate-800 px-2 py-1 rounded text-white">{lang.toUpperCase()}</button>
                                    </div>
                                    <div className="h-px bg-slate-800 my-2"></div>
                                    <button onClick={onLogout} className="w-full text-left px-4 py-3 rounded-xl text-sm font-medium text-red-400 hover:bg-red-500/10 flex items-center gap-3">
                                        <LogOut className="w-4 h-4" /> Sign Out
                                    </button>
                                </div>
                            </MotionDiv>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </header>

        <main className="flex-1 overflow-hidden relative bg-slate-950">
             {children}
        </main>

        <footer className="h-9 bg-slate-950 border-t border-slate-800 flex items-center justify-between px-6 md:px-8 shrink-0 text-[10px] font-mono select-none z-20">
             <div className="flex items-center gap-4 text-slate-500">
                 <div className="flex items-center gap-1.5 text-emerald-500"><Activity className="w-3 h-3" /><span className="tracking-wider font-bold">SYSTEM ONLINE</span></div>
             </div>
             <div className="text-slate-600 flex items-center gap-4"><span className="flex items-center gap-1.5"><ShieldCheck className="w-3 h-3" /> SECURE</span><span>v3.0.0-clean</span></div>
        </footer>

        {/* --- AI CHAT FLOATING BUTTON & WINDOW --- */}
        <AnimatePresence>
          {isAIOpen && (
            <MotionDiv
              initial={{ opacity: 0, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.9 }}
              className="absolute bottom-20 right-8 w-96 h-[500px] bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl overflow-hidden z-50 flex flex-col"
            >
              <AIAssistant onClose={() => setIsAIOpen(false)} />
            </MotionDiv>
          )}
        </AnimatePresence>

        <button 
          onClick={() => setIsAIOpen(!isAIOpen)}
          className={`absolute bottom-6 right-8 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center transition-all hover:scale-110 z-50 border border-white/10 ${isAIOpen ? 'bg-slate-800 text-white' : 'bg-gradient-to-br from-emerald-500 to-teal-600 text-white'}`}
        >
          {isAIOpen ? <X className="w-6 h-6" /> : <Sparkles className="w-6 h-6" />}
        </button>

      </div>
    </div>
  );
};